import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { ClickAwayListener } from "@fidget/common/components/form/clickAwayListener";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import {
  getEmptyWorkingDays,
  WorkingInterval,
  WorkingWeek
} from "@fidget/common/utils/getWorkingHours";
import settingsStyles from "../../../../styles/settings.css";
import { AgentButton } from "@fidget/common/components/inputs/buttons/agentButton/agentButton";
import { PlaceholderDropdown } from "../../../forms/placeholderDropdown/placeholderDropdown";
import { WorkingDay } from "@fidget/common/models/workingDay";
import { WorkingHoursRow } from "../../workingHours/workingHoursRow";
import styles from "./hoursPicker.css";
import stylesWorkingHours from "../../workingHours/workingHoursContainer.css";

interface Props {
  onPicked(intervals: WorkingWeek): void;
  onOpenedChange(): void;
  className?: string;
}
export const HoursPicker = observer(({ onPicked, onOpenedChange, className }: Props) => {
  const [days] = useState(getEmptyWorkingDays());
  const enabledAll = days.every((day) => !day.isDisabled);

  const handleDayChange = (index: number, newInterval: WorkingInterval) => {
    days[index]!.setValues(newInterval);
    if (!days[index]!.isDisabled) {
      days[index]!.setPreviousDayInterval(newInterval);
      while (++index < 7 && days[index]!.isDisabled) {
        days[index]!.setPreviousDayInterval(newInterval);
      }
    }
  };

  const handleApply = () => {
    const mapDayToInterval = (day: WorkingDay) => {
      return { from: day.interval.from.$, to: day.interval.to.$ };
    };

    onPicked(days.map(mapDayToInterval));
  };

  const enableAll = () => {
    days.filter((day) => day.isDisabled).forEach((day) => day.setValues(day.previousDayInterval));
  };

  const disableAll = () => {
    days.forEach((day) => {
      day.setValues({ from: day.dayStart, to: day.dayStart });
    });
  };

  return (
    <ClickAwayListener onClickAway={onOpenedChange}>
      <div className={classNames(styles.container, className)}>
        <PlaceholderDropdown
          text={intl.formatMessage("settings.tagGroups.action.setHours")}
          onClick={onOpenedChange}
          className={settingsStyles["input-width-bigger"]}
        />
        <div
          className={classNames(styles["hours-container"], settingsStyles["input-width-bigger"])}
        >
          <div className={stylesWorkingHours["days-wrapper"]}>
            <div className={styles.buttons}>
              <AgentButton
                buttonStyle="transparent secondary"
                onClick={enabledAll ? disableAll : enableAll}
              >
                <L10n
                  id={
                    enabledAll
                      ? "settings.workingHours.disableAll"
                      : "settings.workingHours.enableAll"
                  }
                />
              </AgentButton>
              <AgentButton buttonStyle="transparent primary" onClick={handleApply}>
                <L10n id={"settings.set"} />
              </AgentButton>
            </div>
            {days.map((day, index) => (
              <WorkingHoursRow
                day={day}
                onChange={(interval) => handleDayChange(index, interval)}
                key={day.name}
                timePickerWidth="auto"
              />
            ))}
          </div>
        </div>
      </div>
    </ClickAwayListener>
  );
});
